// src/components/SliceMachine/NeumoContact/NeumoContact.tsx
'use client';

import React, { useState, useCallback } from 'react';
import Link from 'next/link';
import {
    Phone,
    Mail,
    MapPin,
    MessageCircle,
    CheckCircle2,
    AlertCircle,
    Loader2,
} from 'lucide-react';
import classes from './NeumoContact.module.scss';

type NeumoContactStat = {
    value: string;
    label: string;
};

type NeumoContactItemType = 'phone' | 'whatsapp' | 'email' | 'location';

type NeumoContactItem = {
    type: NeumoContactItemType;
    label: string;
    value: string;
    href?: string;
};

export type NeumoContactSlice = {
    type: 'neumo-contact';
    badge?: string;
    catchphrase?: string;
    title: string;
    description: string;
    stats?: NeumoContactStat[];

    // CTAs (support both legacy primary* and cta* from JSON)
    primaryLabel?: string;
    primaryHref?: string;
    secondaryLabel?: string;
    secondaryHref?: string;
    ctaLabel?: string;
    ctaHref?: string;

    contacts?: NeumoContactItem[];
    note?: string;

    // Optional form configuration (all coming from JSON)
    formTitle?: string;
    formSubtitle?: string;
    formSubmitLabel?: string;
    formAction?: string;

    formNameLabel?: string;
    formNamePlaceholder?: string;

    formEmailLabel?: string;
    formEmailPlaceholder?: string;

    formTopicLabel?: string;
    formTopicPlaceholder?: string;

    formMessageLabel?: string;
    formMessagePlaceholder?: string;

    formHelperText?: string;

    // New: translatable success / error messages
    formSuccessMessage?: string;
    formErrorMessage?: string;
};

export function NeumoContact({ data }: { data: NeumoContactSlice }) {
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMessage('');

        const form = e.currentTarget;
        const formData = new FormData(form);
        const payload = {
            name: String(formData.get('name') || '').trim(),
            email: String(formData.get('email') || '').trim(),
            topic: String(formData.get('topic') || '').trim(),
            message: String(formData.get('message') || '').trim(),
        };

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                setStatus('success');
                form.reset();
            } else {
                const data = await res.json().catch(() => ({}));
                console.error('Contact form error:', data);
                setStatus('error');
                setErrorMessage(data.message || '');
            }
        } catch (err) {
            console.error('Contact form network error:', err);
            setStatus('error');
            setErrorMessage('');
        }
    }, []);

    const {
        badge,
        catchphrase,
        title,
        description,
        stats = [],

        primaryLabel,
        primaryHref,
        secondaryLabel,
        secondaryHref,
        ctaLabel,
        ctaHref,

        contacts = [],
        note,

        formTitle,
        formSubtitle,
        formSubmitLabel,
        formAction = '/api/contact',

        formNameLabel,
        formNamePlaceholder,

        formEmailLabel,
        formEmailPlaceholder,

        formTopicLabel,
        formTopicPlaceholder,

        formMessageLabel,
        formMessagePlaceholder,

        formHelperText,
        formSuccessMessage,
        formErrorMessage,
    } = data;

    // Support both primary* and cta* fields from JSON
    const effectivePrimaryLabel = primaryLabel ?? ctaLabel;
    const effectivePrimaryHref = primaryHref ?? ctaHref;

    const hasPrimary = Boolean(effectivePrimaryLabel && effectivePrimaryHref);
    const hasSecondary = Boolean(secondaryLabel && secondaryHref);

    // Form texts – all overridable via JSON
    const resolvedFormTitle = formTitle ?? title;
    const resolvedFormSubtitle = formSubtitle ?? description;
    const resolvedFormSubmitLabel =
        formSubmitLabel ?? effectivePrimaryLabel ?? 'Send message';

    const resolvedNameLabel = formNameLabel ?? 'Name';
    const resolvedNamePlaceholder = formNamePlaceholder ?? 'Your name';

    const resolvedEmailLabel = formEmailLabel ?? 'Email';
    const resolvedEmailPlaceholder =
        formEmailPlaceholder ?? 'contact@hestia-innovation.com';

    const resolvedTopicLabel = formTopicLabel ?? 'Project / topic';
    const resolvedTopicPlaceholder =
        formTopicPlaceholder ?? 'Website, automation, audit…';

    const resolvedMessageLabel = formMessageLabel ?? 'Message';
    const resolvedMessagePlaceholder =
        formMessagePlaceholder ??
        'Tell us about your project, timelines and tools…';

    const resolvedHelperText =
        formHelperText ??
        note ??
        'We usually reply within 24h on business days.';

    const resolvedSuccessMessage =
        formSuccessMessage ??
        'Thank you, your message has been sent. We’ll get back to you shortly.';

    const resolvedErrorMessage =
        formErrorMessage ??
        'Something went wrong. Please try again or email us directly.';

    const renderContactIcon = (type: NeumoContactItemType) => {
        switch (type) {
            case 'phone':
                return <Phone size={18} />;
            case 'whatsapp':
                return <MessageCircle size={18} />;
            case 'email':
                return <Mail size={18} />;
            case 'location':
            default:
                return <MapPin size={18} />;
        }
    };

    return (
        <section className={classes.hero} aria-labelledby="contact-neumo-title">
            <div className={classes.container}>
                {/* LEFT COLUMN – text, stats, CTAs, quick contacts */}
                <div className={classes.left}>
                    {catchphrase && (
                        <p className={classes.catchphrase}>{catchphrase}</p>
                    )}

                    <h1 id="contact-neumo-title" className={classes.title}>
                        {title}
                    </h1>

                    <p className={classes.description}>{description}</p>

                    {stats.length > 0 && (
                        <div className={classes.statsGrid}>
                            {stats.map((stat, index) => (
                                <div key={index} className={classes.statCard}>
                                    <div className={classes.statValue}>
                                        {stat.value}
                                    </div>
                                    <div className={classes.statLabel}>
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {(hasPrimary || hasSecondary) && (
                        <div className={classes.ctas}>
                            {hasPrimary &&
                                effectivePrimaryLabel &&
                                effectivePrimaryHref && (
                                    <Link
                                        href={effectivePrimaryHref}
                                        className={`${classes.cta} ${classes.primary}`}
                                    >
                                        {effectivePrimaryLabel}
                                    </Link>
                                )}
                            {hasSecondary && (
                                <Link
                                    href={secondaryHref!}
                                    className={`${classes.cta} ${classes.secondary}`}
                                >
                                    {secondaryLabel}
                                </Link>
                            )}
                        </div>
                    )}

                    {contacts.length > 0 && (
                        <div className={classes.contactActions}>
                            {contacts.map((contact, index) =>
                                contact.href ? (
                                    <a
                                        key={`${contact.type}-${index}`}
                                        href={contact.href}
                                        className={classes.contactButton}
                                        aria-label={`${contact.label}: ${contact.value}`}
                                    >
                                        {renderContactIcon(contact.type)}
                                        <span>{contact.value}</span>
                                    </a>
                                ) : (
                                    <div
                                        key={`${contact.type}-${index}`}
                                        className={classes.contactButton}
                                        aria-label={`${contact.label}: ${contact.value}`}
                                    >
                                        {renderContactIcon(contact.type)}
                                        <span>{contact.value}</span>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN – AI glow + FORM CARD ONLY */}
                <div className={classes.right}>
                    {/* Glow background */}
                    <div className={classes.aiHeadVisual} aria-hidden="true">
                        <div className={classes.aiHeadGlow} />
                    </div>

                    <div className={classes.formCard}>
                        <div className={classes.formHeader}>
                            {badge && (
                                <div className={classes.formBadge}>
                                    <span>{badge}</span>
                                </div>
                            )}

                            <h2 className={classes.formTitle}>
                                {resolvedFormTitle}
                            </h2>

                            {resolvedFormSubtitle && (
                                <p className={classes.formSubtitle}>
                                    {resolvedFormSubtitle}
                                </p>
                            )}
                        </div>

                        <form
                            action={formAction}
                            method="post"
                            className={classes.formBody}
                            onSubmit={handleSubmit}
                        >
                            <div className={classes.formGrid}>
                                <div className={classes.field}>
                                    <label
                                        htmlFor="contact-name"
                                        className={classes.label}
                                    >
                                        {resolvedNameLabel}
                                    </label>
                                    <input
                                        id="contact-name"
                                        name="name"
                                        type="text"
                                        required
                                        className={classes.input}
                                        placeholder={resolvedNamePlaceholder}
                                    />
                                </div>

                                <div className={classes.field}>
                                    <label
                                        htmlFor="contact-email"
                                        className={classes.label}
                                    >
                                        {resolvedEmailLabel}
                                    </label>
                                    <input
                                        id="contact-email"
                                        name="email"
                                        type="email"
                                        required
                                        className={classes.input}
                                        placeholder={resolvedEmailPlaceholder}
                                    />
                                </div>

                                <div className={classes.field}>
                                    <label
                                        htmlFor="contact-topic"
                                        className={classes.label}
                                    >
                                        {resolvedTopicLabel}
                                    </label>
                                    <input
                                        id="contact-topic"
                                        name="topic"
                                        type="text"
                                        className={classes.input}
                                        placeholder={resolvedTopicPlaceholder}
                                    />
                                </div>
                            </div>

                            <div className={classes.field}>
                                <label
                                    htmlFor="contact-message"
                                    className={classes.label}
                                >
                                    {resolvedMessageLabel}
                                </label>
                                <textarea
                                    id="contact-message"
                                    name="message"
                                    required
                                    className={classes.textarea}
                                    rows={4}
                                    placeholder={resolvedMessagePlaceholder}
                                />
                            </div>

                            <div className={classes.submitRow}>
                                <button
                                    type="submit"
                                    className={classes.submitButton}
                                    disabled={status === 'loading'}
                                >
                                    {status === 'loading' ? (
                                        <>
                                            <Loader2 size={16} className={classes.spin} />
                                            Envoi…
                                        </>
                                    ) : (
                                        resolvedFormSubmitLabel
                                    )}
                                </button>

                                <p className={classes.helperText}>
                                    {resolvedHelperText}
                                </p>

                                {status === 'success' && (
                                    <div
                                        className={`${classes.statusBanner} ${classes.statusSuccess}`}
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <CheckCircle2 size={16} />
                                        <span>{resolvedSuccessMessage}</span>
                                    </div>
                                )}

                                {status === 'error' && (
                                    <div
                                        className={`${classes.statusBanner} ${classes.statusError}`}
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <AlertCircle size={16} />
                                        <span>
                                            {errorMessage || resolvedErrorMessage}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
